import React from 'react';
import { usePageMeta } from '../utils/usePageMeta';
import { Eyebrow } from '../components/Eyebrow';
import { Button } from '../components/Button';

export const About: React.FC = () => {
  usePageMeta({
    title: 'About Lot 20 Coffee - Farmer-Owned Kenyan Coffee Producer',
    description: 'Learn about Lot 20 Coffee Farmers Services — a producer-owned company operating coffee factories across Kenya\'s western highlands in Kericho, Nandi, and Bomet.',
    canonicalPath: '/about'
  });

  return (
    <>
      {/* Hero */}
      <section className="pt-3 pb-16 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Eyebrow className="mb-3">About Us</Eyebrow>
          <h1 className="font-display text-4xl md:text-5xl text-[#404040] mb-6 max-w-3xl">
            Driven by Curiosity and Craft
          </h1>
          <p className="font-display text-lg text-[#6B6B6B] max-w-2xl leading-relaxed">
            Great coffee comes from curiosity. We're always experimenting with new fermentations,
            different yeasts, co-ferments with fruits, even our own cultured fungi — all to create
            flavours you won't find anywhere else.
          </p>
        </div>
      </section>

      {/* A Clear Purpose */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-16 lg:gap-24 items-start">
            <div>
              <Eyebrow className="mb-3">Our Approach</Eyebrow>
              <h2 className="font-display text-3xl md:text-4xl text-[#404040] mb-6">
                A Clear Purpose
              </h2>
              <p className="font-display text-[#6B6B6B] leading-relaxed mb-6">
                We're not traders. We grow, process, and ship our own coffee. From picking the ripest
                cherries to export, we keep full control so we can deliver coffee that's both unique
                and consistent.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Full vertical integration', desc: 'from farm to export' },
                  { title: 'Quality control', desc: 'at every step of the process' },
                  { title: 'Consistent innovation', desc: 'with reliable results' },
                ].map(item => (
                  <div key={item.title} className="flex items-start gap-4">
                    <span className="w-1.5 h-1.5 bg-[#404040] rounded-full mt-2.5 shrink-0" />
                    <p className="font-display text-[#6B6B6B]">
                      <span className="font-medium text-[#404040]">{item.title}</span> — {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            {/* Process timeline */}
            <div className="space-y-0 border border-[#E8E6E2]">
              {[
                { step: '01', title: 'Cherry selection', desc: 'Only fully ripe, hand-sorted cherries' },
                { step: '02', title: 'Sorting & weighing', desc: 'Mbuni diversion, waste removed' },
                { step: '03', title: 'Fermentation', desc: 'Controlled anaerobic or aerobic tanks' },
                { step: '04', title: 'Drying', desc: 'Raised beds, target 11% moisture' },
                { step: '05', title: 'Milling & grading', desc: 'AA / AB / PB / C for export' },
              ].map(({ step, title, desc }, i, arr) => (
                <div
                  key={step}
                  className={`flex items-start gap-5 px-6 py-5 ${i < arr.length - 1 ? 'border-b border-[#E8E6E2]' : ''}`}
                >
                  <span className="font-mono text-[11px] text-[#A3A3A3] mt-0.5 shrink-0">{step}</span>
                  <div>
                    <p className="text-sm font-medium text-[#404040]">{title}</p>
                    <p className="text-xs text-[#6B6B6B] mt-0.5">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Beyond the Cup */}
      <section className="py-24 md:py-32 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <Eyebrow className="mb-3">What We Do</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-[#404040]">Beyond the Cup</h2>
            <p className="font-display text-lg text-[#6B6B6B] max-w-2xl mt-4">
              Coffee is at the heart of what we do, but we've built projects that go further —
              improving every step of the chain.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Experimental Processing',
                description: 'Advanced fermentation techniques, co-ferments with fruits, and our own cultured fungi to create unique flavor profiles.',
              },
              {
                title: 'Bespoke Decaf Service',
                description: 'Custom decaffeination service for our coffees in Europe, maintaining the unique characteristics of each lot.',
              },
              {
                title: 'Coffee Nursery',
                description: 'Growing strong, high-quality plants for the future, ensuring sustainable coffee production for years to come.',
              },
            ].map(item => (
              <div key={item.title} className="border border-[#E8E6E2] p-8">
                <h3 className="text-xl font-display text-[#404040] mb-3">{item.title}</h3>
                <p className="font-display text-sm text-[#6B6B6B] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnerships */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <Eyebrow className="mb-3">Partnerships</Eyebrow>
            <h2 className="font-display text-3xl md:text-4xl text-[#404040]">Working With Intent</h2>
            <p className="font-display text-lg text-[#6B6B6B] max-w-2xl mt-4">
              We only work with partners who get what we're about. We grow slowly and strategically
              so every lot, and every partnership, gets the attention it deserves.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                name: 'Omwani Coffee Partnership',
                region: 'United Kingdom',
                description: 'Our relationship with Omwani Coffee shows what\'s possible when values align — trust, quality, and ambition on both sides.',
                note: 'Strategic partnership built on shared values',
              },
              {
                name: 'Alpine Coffee Direct',
                region: 'United States',
                description: 'Working directly with roasters like Alpine Coffee to match our coffees to the specific flavors they\'re looking for.',
                note: 'Direct collaboration for perfect flavor matching',
              },
            ].map(partner => (
              <div key={partner.name} className="border border-[#E8E6E2] p-8">
                <Eyebrow size="sm" className="mb-2">{partner.region}</Eyebrow>
                <h3 className="text-xl font-display text-[#404040] mb-3">{partner.name}</h3>
                <p className="font-display text-sm text-[#6B6B6B] leading-relaxed mb-4">{partner.description}</p>
                <p className="text-xs text-[#A3A3A3]">{partner.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#404040]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-6">
            <span className="text-brand-accent">Partner with us —</span> ready to work together?
          </h2>
          <p className="font-display text-[#A3A3A3] leading-relaxed mb-10 max-w-xl mx-auto">
            Join our network of forward-thinking roasters who value innovation, quality,
            and direct relationships with coffee producers.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button to="/coffees" variant="primary" size="md">Explore Our Coffees</Button>
            <Button to="/contact" variant="outline-light" size="md">Start a Partnership</Button>
          </div>
        </div>
      </section>
    </>
  );
};
